import { useState, useEffect, useCallback } from "react";
import type { RedemptionOrder, OrderStatus } from "../utils/rewardsStore";
import { fetchTenantRedemptionOrders, updateRedemptionOrderApi } from "../../../api/redemptionOrders";
import { getUserFacingMessage, reportError } from "../../../api/errors";

type FilterStatus = "all" | OrderStatus;

const STATUS_CONFIG: Record<OrderStatus, { label: string; badge: string; dot: string }> = {
  pending: { label: "待发货", badge: "bg-amber-50 text-amber-700", dot: "bg-amber-400" },
  shipped: { label: "已发货", badge: "bg-blue-50 text-blue-700", dot: "bg-blue-400" },
  completed: { label: "已完成", badge: "bg-green-50 text-green-700", dot: "bg-green-400" },
};

interface ShipModalState {
  orderId: string;
  memberName: string;
  rewardName: string;
  trackingNo: string;
}

export default function RedeemOrders({ tenantLegacyId }: { tenantLegacyId: string }) {
  const [orders, setOrders] = useState<RedemptionOrder[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterStatus>("all");
  const [shipModal, setShipModal] = useState<ShipModalState | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoadError(null);
    try {
      const rows = await fetchTenantRedemptionOrders(tenantLegacyId);
      setOrders(rows);
    } catch (e) {
      setLoadError(getUserFacingMessage(e, "兑换订单加载失败，请刷新。"));
    }
  }, [tenantLegacyId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const displayed = filter === "all" ? orders : orders.filter((o) => o.status === filter);

  const countByStatus = (s: OrderStatus) => orders.filter((o) => o.status === s).length;

  const handleShipConfirm = async () => {
    if (!shipModal) return;
    setBusy(true);
    try {
      await updateRedemptionOrderApi(tenantLegacyId, shipModal.orderId, {
        status: "shipped",
        trackingNo: shipModal.trackingNo.trim() || null,
      });
      await reload();
      setShipModal(null);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };

  const handleComplete = async (id: string) => {
    setBusy(true);
    try {
      await updateRedemptionOrderApi(tenantLegacyId, id, { status: "completed" });
      await reload();
      setCompletingId(null);
    } catch (e) {
      reportError(e);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-5 gap-4 flex-wrap">
        <div>
          <h2 className="text-lg font-bold text-gray-800">兑换订单</h2>
          <p className="text-xs text-gray-400 mt-0.5">与会员中心积分商城联动，发货后会员可查看物流单号</p>
          {loadError && <p className="text-xs text-red-500 mt-1">加载失败：{loadError}</p>}
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => void reload()}
            disabled={busy}
            className="text-xs px-3 py-1.5 rounded-xl border border-gray-200 text-gray-600 hover:bg-gray-50 cursor-pointer disabled:opacity-50"
          >
            <i className="ri-refresh-line mr-1" />
            刷新
          </button>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-amber-400 rounded-full"></span>
              待发货 <strong className="text-amber-600 ml-0.5">{countByStatus("pending")}</strong>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
              已发货 <strong className="text-blue-600 ml-0.5">{countByStatus("shipped")}</strong>
            </span>
            <span className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              已完成 <strong className="text-green-600 ml-0.5">{countByStatus("completed")}</strong>
            </span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3 mb-5">
        {[
          { label: "订单总数", value: orders.length, icon: "ri-shopping-bag-3-line", color: "bg-gray-50 text-gray-600" },
          { label: "待处理", value: countByStatus("pending"), icon: "ri-time-line", color: "bg-amber-50 text-amber-600" },
          { label: "运输中", value: countByStatus("shipped"), icon: "ri-truck-line", color: "bg-blue-50 text-blue-600" },
          { label: "已完成", value: countByStatus("completed"), icon: "ri-check-double-line", color: "bg-green-50 text-green-600" },
        ].map((s) => (
          <div key={s.label} className="bg-white rounded-2xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-lg`}></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">{s.value}</p>
              <p className="text-xs text-gray-500">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        <div className="px-5 pt-4 pb-3 border-b border-gray-50 flex items-center gap-2">
          <div className="flex gap-1 bg-gray-100 p-1 rounded-xl">
            {(["all", "pending", "shipped", "completed"] as FilterStatus[]).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFilter(s)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium cursor-pointer transition-all whitespace-nowrap ${
                  filter === s ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {s === "all" ? "全部" : STATUS_CONFIG[s].label}
                <span className="ml-1 text-gray-400">{s === "all" ? orders.length : countByStatus(s)}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50">
                {["兑换商品", "会员信息", "消耗积分", "收货地址", "兑换时间", "物流单号", "状态", "操作"].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-gray-500 whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {displayed.map((order) => {
                const st = STATUS_CONFIG[order.status];
                return (
                  <tr key={order.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-3">
                        <div className="w-11 h-11 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
                          <img
                            src={order.rewardCover}
                            alt={order.rewardName}
                            className="w-full h-full object-cover object-top"
                          />
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate max-w-[140px]">{order.rewardName}</p>
                          <p className="text-xs text-gray-400 font-mono mt-0.5">{order.id}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2.5">
                        <div className="w-7 h-7 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <i className="ri-user-line text-red-500 text-xs"></i>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800 text-sm">{order.memberName}</p>
                          <p className="text-xs text-gray-400 font-mono">{order.memberNo}</p>
                          <p className="text-[10px] text-gray-400 font-mono">{order.memberId}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span className="font-bold text-red-600 text-sm">-{order.pointsCost}</span>
                      <span className="text-xs text-gray-400 ml-1">积分</span>
                    </td>
                    <td className="px-5 py-4">
                      <p className="text-xs text-gray-600 max-w-[140px] leading-relaxed truncate" title={order.address}>
                        {order.address || <span className="text-gray-300">未填写</span>}
                      </p>
                    </td>
                    <td className="px-5 py-4 text-xs text-gray-500 whitespace-nowrap">{order.createdAt}</td>
                    <td className="px-5 py-4">
                      {order.trackingNo ? (
                        <span className="text-xs font-mono text-gray-700 bg-gray-50 px-2 py-1 rounded-lg">{order.trackingNo}</span>
                      ) : (
                        <span className="text-xs text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${st.badge}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${st.dot}`}></span>
                        {st.label}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {order.status === "pending" && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() =>
                              setShipModal({
                                orderId: order.id,
                                memberName: order.memberName,
                                rewardName: order.rewardName,
                                trackingNo: "",
                              })
                            }
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
                          >
                            <i className="ri-truck-line"></i>发货
                          </button>
                        )}
                        {order.status === "shipped" && (
                          <button
                            type="button"
                            disabled={busy}
                            onClick={() => setCompletingId(order.id)}
                            className="flex items-center gap-1 px-2.5 py-1.5 bg-green-50 hover:bg-green-100 text-green-600 rounded-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap disabled:opacity-50"
                          >
                            <i className="ri-check-line"></i>确认完成
                          </button>
                        )}
                        {order.status === "completed" && <span className="text-xs text-gray-400">已完成</span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {displayed.length === 0 && (
            <div className="py-16 text-center">
              <i className="ri-shopping-bag-3-line text-4xl text-gray-200 block mb-3"></i>
              <p className="text-sm text-gray-400">暂无兑换订单</p>
            </div>
          )}
        </div>
      </div>

      {shipModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => !busy && setShipModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-100">
              <div>
                <h3 className="font-bold text-gray-900 text-sm">确认发货</h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {shipModal.memberName} · {shipModal.rewardName}
                </p>
              </div>
              <button
                type="button"
                disabled={busy}
                onClick={() => setShipModal(null)}
                className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer disabled:opacity-50"
              >
                <i className="ri-close-line text-gray-500 text-sm"></i>
              </button>
            </div>
            <div className="px-5 py-4 space-y-3">
              <div className="bg-amber-50 rounded-xl px-4 py-3 flex items-start gap-2">
                <i className="ri-truck-line text-amber-500 text-base mt-0.5 flex-shrink-0"></i>
                <p className="text-xs text-amber-700 leading-relaxed">发货后会员在「积分兑换 · 我的订单」中可看到物流信息。</p>
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1.5">物流单号（选填）</label>
                <input
                  type="text"
                  value={shipModal.trackingNo}
                  onChange={(e) => setShipModal({ ...shipModal, trackingNo: e.target.value })}
                  placeholder="如：SF1234567890"
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-red-400"
                />
              </div>
            </div>
            <div className="flex gap-3 px-5 pb-5">
              <button
                type="button"
                disabled={busy}
                onClick={() => setShipModal(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium cursor-pointer hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleShipConfirm()}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-red-700 whitespace-nowrap disabled:opacity-50"
              >
                <i className="ri-truck-line mr-1"></i>
                {busy ? "提交中…" : "确认发货"}
              </button>
            </div>
          </div>
        </div>
      )}

      {completingId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-2xl w-full max-w-xs shadow-xl p-6 text-center">
            <div className="w-12 h-12 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-3">
              <i className="ri-check-double-line text-green-500 text-xl"></i>
            </div>
            <h3 className="font-bold text-gray-900 mb-1">标记为已完成？</h3>
            <p className="text-xs text-gray-400 mb-5">确认后订单状态将更新为「已完成」</p>
            <div className="flex gap-3">
              <button
                type="button"
                disabled={busy}
                onClick={() => setCompletingId(null)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap disabled:opacity-50"
              >
                取消
              </button>
              <button
                type="button"
                disabled={busy}
                onClick={() => void handleComplete(completingId)}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-xl text-sm font-medium cursor-pointer hover:bg-green-700 whitespace-nowrap disabled:opacity-50"
              >
                {busy ? "…" : "确认完成"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
